'use client'

import { usePathname } from 'next/navigation'
import { setCookie } from 'nookies'
import { useCallback } from 'react'
import { useTranslation as useI18nextTranslation } from 'react-i18next'

import i18nConfig, { LOCALE_COOKIE } from './i18nConfig'
import type { Locale } from './types'
import { useRouterContext } from '~/lib/next/use-router-context'
import { isCareerHubApp, isProtectedRoute } from '~/lib/utilities/pathname'

export function useTranslation(namespace?: string | string[]) {
  const { t, i18n, ready } = useI18nextTranslation(namespace)

  return {
    t: (key: string, options?: any): string => {
      try {
        return t(key, options) as string
      } catch (error) {
        console.warn(`Translation missing for key: ${key}`, error)
        return key
      }
    },
    i18n: {
      language: i18n.language,
      changeLanguage: i18n.changeLanguage.bind(i18n)
    },
    ready
  }
}

// Language switcher hook with debugging
export function useLanguageSwitcher() {
  const { searchParams, pathname, pathnameWithoutLocale } = useRouterContext()

  const { i18n } = useTranslation()

  const changeLanguage = useCallback(
    async (newLocale: Locale) => {
      try {
        // Set cookie FIRST before any other operations
        await setCookie(null, LOCALE_COOKIE, newLocale, {
          maxAge: 365 * 24 * 60 * 60,
          path: '/',
          sameSite: 'lax'
        })

        // Then change language
        // No need to await i18n change in this case because we've method init i18n when the page has reloaded
        // await i18n.changeLanguage(newLocale)

        let newPathname = pathname ?? ''
        const currentQueries = searchParams?.toString()

        // Remove current locale from path if exists
        const currentLocaleInPath = i18nConfig.locales.find(
          (locale) => pathname?.startsWith(`/${locale}/`) || pathname === `/${locale}`
        )

        if (currentLocaleInPath) {
          newPathname = pathname?.replace(new RegExp(`^/${currentLocaleInPath}`), '') || '/'
        }

        // Add new locale to path based on config
        if (newLocale !== i18nConfig.defaultLocale || i18nConfig.prefixDefault) {
          newPathname = `/${newLocale}${newPathname}`
        }

        if (isCareerHubApp(pathnameWithoutLocale) && !isProtectedRoute(pathnameWithoutLocale)) {
          window.history.replaceState(
            window.history.state,
            '',
            currentQueries ? `${newPathname}?${currentQueries}` : newPathname
          )
          await i18n.changeLanguage(newLocale)
          return
        }

        // Navigate last
        if (newPathname !== pathname) {
          window.location.href = currentQueries ? `${newPathname}?${currentQueries}` : newPathname
        } else {
          window.location.reload()
        }
      } catch (error) {
        console.error('❌ Language change failed:', error)
      }
    },
    [i18n, pathname, pathnameWithoutLocale, searchParams]
  )

  return {
    currentLocale: i18n.language as Locale,
    changeLanguage
  }
}
